import CrossIcon from "@assets/icons/cross.svg";
import { useI18n } from "@core/i18n/i18n";
import { useExerciseFilters } from "@ui/components/exercise/hooks/use-exercise-filters";
import styled from "@emotion/styled";
import { ButtonIcon } from "@ui/components/buttons/button-icon";
import { physioMeMobileBreakpoint, row, stack, TABLET_BREAKING_POINT } from "@ui/style";
import { colors } from "@ui/style/colors";
import { OrderedMap } from "immutable";
import { OverlayPanel, OverlayPanelProps } from "primereact/overlaypanel";
import React, { Fragment, ReactElement, Ref, useImperativeHandle, useRef, useMemo, useEffect, useState } from "react";
import { FilterItem, FilterItemStatus } from "@ui/components/filters/filter-item";
import { FilterCategory } from "@domain/filters/filter";
import { Filter } from "@domain/filters/filter";
import { useObservable } from "micro-observables";
import { libraryFilterManager } from "@core/service-container";

interface FilterPanelProps<T extends Filter> extends Omit<OverlayPanelProps, "title"> {
  filterOptions: FilterCategory<T>[];
  activeFilters: FilterCategory<T>[];
  title: React.ReactNode;
  onActiveFiltersChange: (filters: FilterCategory<T>[]) => void;
  onFilterChange?: (filter: T & { categoryId: string; active: boolean }) => void;
}

const FilterPanelComponent = <T extends Filter>(
  { filterOptions, title, onActiveFiltersChange, onFilterChange, ...otherProps }: FilterPanelProps<T>,
  ref?: Ref<OverlayPanel>,
) => {
  const { format } = useI18n();
  const panelRef = useRef<OverlayPanel>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useImperativeHandle<OverlayPanel | null, OverlayPanel | null>(ref, () => panelRef.current);

  const activeCategoryFilters2 = useObservable(libraryFilterManager.activeCategoryFilters) as unknown as FilterCategory<T>[];

  const exerciseFilters = useExerciseFilters() as unknown as FilterCategory<T>[];

  const displayFilters = filterOptions.length > 0 ? filterOptions : exerciseFilters;

  const [offsetTop, setOffsetTop] = useState(0);

  const activeFiltersMap: InternalFiltersMap<T> = useMemo(() =>
    OrderedMap(
      activeCategoryFilters2.map(category => [
        category.id,
        { ...category, filters: OrderedMap(category.filters.map(filter => [filter.id, filter])) },
      ])
    ),
    [activeCategoryFilters2]);

  useEffect(() => {
    console.log("activeCategoryFilters2 changed:", activeCategoryFilters2);
  }, [activeCategoryFilters2]);

  const handleShow = () => {
    otherProps.onShow?.();
    const element = (wrapperRef.current as unknown as { container?: HTMLElement }).container || wrapperRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const absoluteY = rect.top + window.scrollY;
    setOffsetTop(absoluteY);
  };

  return (
    <div ref={wrapperRef}>
      <StyledOverlayPanel ref={panelRef} dismissable {...otherProps} $offsetTop={offsetTop} onShow={handleShow}>
        <Container>
          <ButtonContainer>
            <ButtonIcon
              icon={CrossIcon}
              onClick={() => panelRef.current?.hide()}
              size="condensed"
            />
          </ButtonContainer>
          <TitleContainer>
            <div className="p-size-l-bold">{title}</div>
            <ClickableText onClick={() => onActiveFiltersChange([])}>
              {format("exerciseFilterPanel.clear")}
            </ClickableText>
          </TitleContainer>

          {displayFilters.map((category) => (
            <Fragment key={category.id}>
              <div className="p-size-m-bold">{category.label}</div>
              <FilterContainer>
                {category.filters.map(filter => {
                  const isActive = activeFiltersMap.get(category.id)?.filters.has(filter.id) ?? false;
                  const status = isActive ? FilterItemStatus.Active : FilterItemStatus.Inactive;

                  const handleFilterChange = (checked: boolean) => {
                    onFilterChange?.({ ...filter, categoryId: category.id, active: checked });

                    const currentCategory = activeFiltersMap.get(category.id) || {
                      ...category,
                      filters: OrderedMap<string, T>()
                    };

                    const newFilters = checked
                      ? currentCategory.filters.set(filter.id, filter)
                      : currentCategory.filters.remove(filter.id);

                    const newMap = activeFiltersMap.set(category.id, {
                      ...currentCategory,
                      filters: newFilters
                    });

                    onActiveFiltersChange(internalFilterMapToArray(newMap));
                  };

                  return (
                    <FilterItem
                      key={filter.id}
                      status={status}
                      filter={filter}
                      onChange={handleFilterChange}
                    />
                  );
                })}
              </FilterContainer>
            </Fragment>
          ))}
        </Container>
      </StyledOverlayPanel>
    </div>
  );
};

export const FilterPanel = React.forwardRef(FilterPanelComponent) as <T extends Filter>(
  props: FilterPanelProps<T> & { ref?: Ref<OverlayPanel> },
) => ReactElement;

interface InternalFilterCategory<T extends Filter> extends Omit<FilterCategory<T>, "filters"> {
  filters: OrderedMap<string, T>;
}

type InternalFiltersMap<T extends Filter> = OrderedMap<string, InternalFilterCategory<T>>;

function internalFilterMapToArray<T extends Filter>(filters: InternalFiltersMap<T>): FilterCategory<T>[] {
  return Array.from(filters.values()).reduce((nonEmptyCategories, category) => {
    const filtersArray = Array.from(category.filters.values());
    if (filtersArray.length === 0) {
      return nonEmptyCategories;
    }

    const result: FilterCategory<T> = {
      ...category,
      filters: filtersArray,
    };

    return [...nonEmptyCategories, result];
  }, [] as FilterCategory<T>[]);
}

const StyledOverlayPanel = styled(OverlayPanel) <{ $offsetTop?: number }>`
  padding: 16px;
  max-width: 600px;
  max-height: ${props => props.$offsetTop ? `calc(100dvh - ${props.$offsetTop}px)` : "100dvh"};
  overflow: auto;

	.p-overlaypanel-content {
		padding: 0;
	}

	@media (min-width: ${TABLET_BREAKING_POINT}px) {
		position: absolute !important;
		left: 405px !important;
		top: 155px !important;
	}

	@media ${physioMeMobileBreakpoint} {
		padding-bottom: 100px;
	}
`;

const Container = styled.div`
	${stack(16, "flexstart", "stretch")}
`;

const FilterContainer = styled.div`
	${row(8)};
	margin-top: -5px;
	margin-bottom: 8px;
	flex-flow: wrap;
`;

const ClickableText = styled.div`
	margin: -5px;
	padding: 5px;
	cursor: pointer;

	:hover {
		color: ${colors.primary};
	}
`;

const ButtonContainer = styled.div`
	${row(0, "flexend", "center")}
`;

const TitleContainer = styled.div`
	${row(0, "spacebetween", "center")}
`;
